/** Width of an element in CSS px, tracked with ResizeObserver; `fallback` where it is unavailable (jsdom, first paint). */
import { useEffect, useRef, useState } from "react";

export function useElementWidth<T extends HTMLElement>(fallback: number): readonly [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (el === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => { if (entry !== undefined) setWidth(Math.round(entry.contentRect.width)); });
    observer.observe(el);
    setWidth(Math.round(el.getBoundingClientRect().width) || fallback);
    return () => observer.disconnect();
  }, [fallback]);
  return [ref, width];
}

/** Width and height of an element in CSS px, tracked with ResizeObserver; the fallbacks stand where it is unavailable (jsdom, first paint). */
export function useElementSize<T extends HTMLElement>(fallback: { readonly width: number; readonly height: number }): readonly [React.RefObject<T | null>, { readonly width: number; readonly height: number }] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (el === null || typeof ResizeObserver === "undefined") return;
    const read = (width: number, height: number) => setSize((prev) => { const next = { width: Math.round(width) || fallback.width, height: Math.round(height) || fallback.height }; return prev.width === next.width && prev.height === next.height ? prev : next; });
    const observer = new ResizeObserver(([entry]) => { if (entry !== undefined) read(entry.contentRect.width, entry.contentRect.height); });
    observer.observe(el);
    const box = el.getBoundingClientRect();
    read(box.width, box.height);
    return () => observer.disconnect();
  }, [fallback.width, fallback.height]);
  return [ref, size];
}
